import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { JsonViewer } from "../base/JsonViewer";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { safeString } from "../../utils/strings";

export interface StartSessionEventData {
  name?: string;
  data?: {
    agent_id?: string;
    environment?: string;
    input_type?: string;
    input_parameters?: Record<string, unknown>;
    parameters?: Record<string, unknown>;
  };
  timestamp?: string;
  turn?: number;
  background?: boolean;
  background_task_name?: string | null;
}

const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  } catch {
    return timestamp;
  }
};

// Content component for the Start Session tab
const StartSessionContent = ({ data }: { data: StartSessionEventData }) => {
  return (
    <View className="space-y-4">
      {/* Metadata row */}
      <View className="flex flex-row flex-wrap gap-4">
        {/* Agent ID */}
        {data.data?.agent_id && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="bot"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Agent: <span className="font-mono text-gray-800 dark:text-gray-200">{safeString(data.data.agent_id)}</span>
            </Text>
          </View>
        )}

        {/* Environment */}
        {data.data?.environment && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="layers"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Environment: <span className="font-medium text-gray-800 dark:text-gray-200">{safeString(data.data.environment)}</span>
            </Text>
          </View>
        )}

        {/* Input Type */}
        {data.data?.input_type && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name={data.data.input_type === "phone" ? "phone" : "keyboard"}
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Input: <span className="font-medium text-gray-800 dark:text-gray-200">{safeString(data.data.input_type)}</span>
            </Text>
          </View>
        )}

        {/* Timestamp */}
        {data.timestamp && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="clock"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">{formatTimestamp(data.timestamp)}</Text>
          </View>
        )}

        {/* Background */}
        {data.background && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="layers"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">Background{data.background_task_name ? `: ${data.background_task_name}` : ""}</Text>
          </View>
        )}
      </View>

      {/* Input Parameters */}
      {data.data?.input_parameters && Object.keys(data.data.input_parameters).length > 0 && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="input"
              size={14}
              className="text-emerald-500 dark:text-emerald-400"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Input Parameters</Text>
          </View>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <JsonViewer
              data={data.data.input_parameters}
              collapsed={5}
              className="p-2"
            />
          </View>
        </View>
      )}

      {/* Parameters */}
      {data.data?.parameters && Object.keys(data.data.parameters).length > 0 && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="code"
              size={14}
              className="text-indigo-500 dark:text-indigo-400"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Parameters</Text>
          </View>
          <View className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800 overflow-hidden">
            <JsonViewer
              data={data.data.parameters}
              collapsed={5}
              className="p-2"
            />
          </View>
        </View>
      )}
    </View>
  );
};

// Main StartSessionEventSpanDetailView component with tabs
interface StartSessionEventSpanDetailViewProps {
  data: StartSessionEventData;
  rawData: RawSpanData;
  className?: string;
}

const tabs: TabDefinition[] = [
  { id: "start_session", label: "Start Session", color: "emerald" },
  { id: "attributes", label: "Attributes", color: "gray" },
];

export const StartSessionEventSpanDetailView = ({ data, rawData, className }: StartSessionEventSpanDetailViewProps) => {
  const [activeTab, setActiveTab] = useState("start_session");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">
        {activeTab === "start_session" ? <StartSessionContent data={data} /> : <RawSpanDetailView data={rawData} />}
      </View>
    </View>
  );
};

// Helper to parse start session event data from span attributes
export const parseStartSessionEventData = (attributes: Record<string, unknown>): StartSessionEventData | null => {
  const outputValue = attributes["output.value"];
  if (typeof outputValue !== "string") return null;

  try {
    const parsed = JSON.parse(outputValue);
    return parsed as StartSessionEventData;
  } catch {
    return null;
  }
};
